/* iter-51 — Admin ISBN Panel.
 *
 * Lists every ISBN record across the platform. Admins can:
 *   - Filter by status (requested / pending / issued / discontinued)
 *   - Issue an ISBN string to a record (validates ISBN-10/13 checksum
 *     server-side, refuses duplicates)
 *   - Edit any metadata field
 *   - Discontinue or delete a record
 *
 * Publisher of Record is hard-coded "Crown & Rose LLC".
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, Hash, RefreshCw, X, Lock, AlertCircle, Image as ImageIcon, FileText, ClipboardCopy, Download, Wallet, Plus, Zap, ListChecks, BookOpen } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { formatIsbn, splitCsv, intOrNull, dollarsToCents } from '@/lib/format';
import { openConfirm } from '@/components/admin/ConfirmDialog';
import IsbnPasteChecklist from './IsbnPasteChecklist';
import IsbnAssignedLedger from './IsbnAssignedLedger';
import BowkerTitleEntry from './BowkerTitleEntry';

const PUBLISHER = 'Crown & Rose LLC';
const FORMATS = ['paperback', 'hardcover', 'audiobook', 'ebook'];

const STATUS_STYLES = {
  pending:      { label: 'Pending',      cls: 'bg-amber-100 text-amber-900 border-amber-300' },
  requested:    { label: 'Requested',    cls: 'bg-sky-100 text-sky-900 border-sky-300' },
  issued:       { label: 'Issued',       cls: 'bg-emerald-100 text-emerald-900 border-emerald-300' },
  discontinued: { label: 'Discontinued', cls: 'bg-rose-100 text-rose-900 border-rose-300' },
};

const FILTERS = [
  { v: '',             label: 'All' },
  { v: 'requested',    label: 'Requested' },
  { v: 'pending',      label: 'Pending' },
  { v: 'issued',       label: 'Issued' },
  { v: 'discontinued', label: 'Discontinued' },
];

export default function IsbnAdminPanel() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState('requested');
  const [busy, setBusy] = useState(false);
  const [activeId, setActiveId] = useState(null);
  /* 2026-02 · Top-level tab. "records" is the original per-record
   * editor view; "ledger" is the new author-grouped assigned view. */
  const [tab, setTab] = useState('records');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const d = await adminApi.listIsbnAll(filter || undefined);
      setRows(d.records || []);
      setActiveId((cur) => cur || d.records?.[0]?.id || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load ISBN records.');
      setRows([]);
    } finally {
      setBusy(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const active = (rows || []).find((r) => r.id === activeId) || null;

  return (
    <div className="space-y-4" data-testid="admin-isbn-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-[16px] font-semibold tracking-tight">
            <Hash className="h-4 w-4 text-primary" /> ISBN Manager
          </h2>
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">
            Issue ISBN-10 / ISBN-13 strings, manage metadata, and discontinue records.
            Publisher of Record is <strong>{PUBLISHER}</strong> for every row.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={busy}
          data-testid="admin-isbn-refresh"
          className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-background px-2.5 py-1.5 text-[11.5px] hover:bg-secondary disabled:opacity-60"
        >
          <RefreshCw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* 2026-02 · Top-level tabs — Records (per-record editor) vs
       *  Ledger (author-grouped assigned view). */}
      <div className="flex items-center gap-1.5 border-b border-border">
        <TabButton active={tab === 'records'} onClick={() => setTab('records')}
                    testid="admin-isbn-tab-records"
                    icon={<ListChecks className="h-3 w-3" />} label="Records" />
        <TabButton active={tab === 'ledger'} onClick={() => setTab('ledger')}
                    testid="admin-isbn-tab-ledger"
                    icon={<BookOpen className="h-3 w-3" />} label="Assigned Ledger" />
      </div>

      {tab === 'ledger' ? (
        <IsbnAssignedLedger />
      ) : (
        <RecordsView rows={rows} filter={filter} setFilter={setFilter}
                       activeId={activeId} setActiveId={setActiveId}
                       load={load} />
      )}
    </div>
  );
}

function TabButton({ active, onClick, testid, icon, label }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            className={`-mb-[1px] inline-flex items-center gap-1.5 border-b-2 px-3 py-1.5 text-[12px] transition ${
              active
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}>
      {icon}
      {label}
    </button>
  );
}

function RecordsView({ rows, filter, setFilter, activeId, setActiveId, load }) {
  const active = (rows || []).find((r) => r.id === activeId) || null;
  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.v || 'all'}
            type="button"
            onClick={() => setFilter(f.v)}
            data-testid={`admin-isbn-filter-${f.v || 'all'}`}
            className={`rounded-sm border px-2.5 py-1 text-[11px] transition ${
              filter === f.v
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-card text-muted-foreground hover:bg-secondary'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Sep-14-2026 — Bowker ISBN Bank inventory + paste-in wizard */}
      <IsbnBankSection onAllocateUpdated={load} />

      <div className="grid grid-cols-12 gap-4">
        <aside className="col-span-5 flex max-h-[640px] flex-col gap-1.5 overflow-y-auto border-r border-border pr-3"
                data-testid="admin-isbn-list">
          {rows === null ? (
            <Loader />
          ) : rows.length === 0 ? (
            <div className="rounded-md border border-dashed border-border bg-card/40 p-6 text-center text-[12px] text-muted-foreground">
              No records under this filter.
            </div>
          ) : (
            rows.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setActiveId(r.id)}
                data-testid={`admin-isbn-row-${r.id}`}
                className={`flex flex-col items-start gap-1 rounded-sm border px-2.5 py-2 text-left transition hover:bg-secondary ${
                  activeId === r.id ? 'border-primary bg-primary/5' : 'border-border bg-card'
                }`}
              >
                <div className="flex w-full items-center justify-between gap-2">
                  <span className="line-clamp-1 text-[12.5px] font-semibold">{r.title || 'Untitled'}</span>
                  <div className="flex shrink-0 items-center gap-1">
                    {r.cover_ready && <CoverReadyBadge testid={`admin-isbn-cover-ready-${r.id}`} />}
                    {r.project_id && !r.manuscript_ready && (
                      <ManuscriptIncompleteBadge testid={`admin-isbn-manuscript-incomplete-${r.id}`} />
                    )}
                    <StatusPill status={r.status} />
                  </div>
                </div>
                <div className="flex flex-col gap-0.5 text-[10.5px] text-muted-foreground">
                  <span>{r.isbn ? `ISBN ${formatIsbn(r.isbn)}` : 'No ISBN assigned'}</span>
                  <span className="opacity-80">
                    Requested by {r.requested_by_name || '—'}
                  </span>
                </div>
              </button>
            ))
          )}
        </aside>

        <section className="col-span-7 min-h-[640px]">
          {!active ? (
            <div className="flex h-full items-center justify-center text-[12px] text-muted-foreground">
              Select a record to edit.
            </div>
          ) : (
            <AdminIsbnEditor record={active} onSaved={load} />
          )}
        </section>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────── editor

function AdminIsbnEditor({ record, onSaved }) {
  const [draft, setDraft] = useState(() => ({ ...record }));
  const [busy, setBusy] = useState(false);
  const [isbnInput, setIsbnInput] = useState('');
  const [showBowker, setShowBowker] = useState(false);

  // Only re-sync when the record identity changes; including `record` itself
  // would overwrite in-progress admin edits on every parent re-render.
  useEffect(() => {
    setDraft({ ...record });
    setIsbnInput('');
    // iter55f58c — full `record` is intentionally excluded — we only
    // reseed the draft when the record's identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.id]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const patch = {
        title:              draft.title,
        subtitle:           draft.subtitle || null,
        contributors:       splitCsv(draft._contributors_text ?? (draft.contributors || []).join(', ')),
        format:             draft.format || null,
        edition:            draft.edition || null,
        language:           draft.language || null,
        trim_size:          draft.trim_size || null,
        page_count:         intOrNull(draft.page_count),
        publication_date:   draft.publication_date || null,
        bisac_codes:        splitCsv(draft._bisac_text ?? (draft.bisac_codes || []).join(', ')),
        retail_price_cents: dollarsToCents(draft._retail_dollars ?? (record.retail_price_cents == null ? '' : (record.retail_price_cents / 100).toFixed(2))),
        currency:           (draft.currency || 'USD').toUpperCase(),
        project_id:         draft.project_id || null,
        bookstore_book_id:  draft.bookstore_book_id || null,
      };
      await adminApi.updateIsbn(record.id, patch);
      toast.success('Saved.');
      await onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  const issue = async () => {
    if (!isbnInput.trim()) {
      toast.error('Enter an ISBN-10 or ISBN-13 to issue.');
      return;
    }
    setBusy(true);
    try {
      const r = await adminApi.issueIsbn(record.id, isbnInput.trim());
      toast.success('ISBN issued.');
      if (r?.cover_promotion?.cover_url) {
        toast.success(`Cover promoted to storefront from Canva.`);
      }
      setIsbnInput('');
      await onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Issue failed (likely checksum or duplicate).');
    } finally {
      setBusy(false);
    }
  };

  const discontinue = async () => {
    if (!(await openConfirm({
      title: 'Discontinue this record?',
      description: 'Authors will see it as Discontinued.',
      confirmLabel: 'Discontinue',
      tone: 'destructive',
    }))) return;
    setBusy(true);
    try {
      await adminApi.discontinueIsbn(record.id);
      toast.success('Discontinued.');
      await onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Discontinue failed.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!(await openConfirm({
      title: 'Delete this record entirely?',
      description: 'This cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'destructive',
    }))) return;
    setBusy(true);
    try {
      await adminApi.removeIsbn(record.id);
      toast.success('Deleted.');
      await onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    } finally {
      setBusy(false);
    }
  };

  const allocateFromBank = async (poolOverride) => {
    setBusy(true);
    try {
      const r = await adminApi.isbnAllocate(record.id, poolOverride);
      toast.success(`Allocated ISBN ${r.record.isbn} from pool "${r.record.pool || r.pool || 'default'}".`);
      if (r?.cover_promotion?.cover_url) {
        toast.success('Cover promoted to storefront from Canva.');
      }
      await onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bank allocation failed.');
    } finally { setBusy(false); }
  };

  const ecoFilingPrep = async () => {
    setBusy(true);
    try {
      const r = await adminApi.isbnEcoFilingPrep(record.id);
      await navigator.clipboard.writeText(r.clipboard);
      toast.success('Copyright filing bundle copied to clipboard.');
      window.open(r.deep_links.start_new_claim, '_blank', 'noopener');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'eCO prep failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col gap-3" data-testid={`admin-isbn-editor-${record.id}`}>
      {/* Issue panel */}
      {record.status !== 'issued' ? (
        <div className="flex flex-wrap items-end gap-2 rounded-md border border-primary/30 bg-primary/5 p-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Issue ISBN string</span>
            <input
              type="text"
              value={isbnInput}
              onChange={(e) => setIsbnInput(e.target.value)}
              placeholder="978-0-306-40615-7"
              data-testid="admin-isbn-issue-input"
              className="h-8 w-64 rounded-sm border border-border bg-background px-2 text-[12px] font-mono outline-none focus:border-primary"
            />
          </div>
          <button
            type="button"
            onClick={issue}
            disabled={busy}
            data-testid="admin-isbn-issue-btn"
            className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            <Hash className="h-3 w-3" /> Issue ISBN
          </button>
          <IsbnAllocateWithPool onAllocate={allocateFromBank} busy={busy} />
          <span className="ml-auto inline-flex items-center gap-1 text-[10.5px] text-muted-foreground">
            <AlertCircle className="h-3 w-3" /> Checksum + duplicate-guard enforced server-side.
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded-md border border-emerald-300/50 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-900">
          <Lock className="h-3.5 w-3.5" />
          ISBN <span className="font-mono font-semibold">{formatIsbn(record.isbn)}</span> issued
          {record.issued_at ? ` on ${record.issued_at.slice(0, 10)}` : ''}
          {record.issued_by ? ` by ${record.issued_by}` : ''}.
        </div>
      )}

      {/* Action row */}
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill status={record.status} />
        {record.isbn && (
          <span className="rounded-sm border border-border bg-secondary px-2 py-[1.5px] text-[11px] font-mono">
            {formatIsbn(record.isbn)}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowBowker(true)}
            disabled={busy}
            data-testid="admin-isbn-bowker-entry-btn"
            title="Open the Bowker Identifier Services title-registration mimic form"
            className="inline-flex items-center gap-1 rounded-sm border border-[#00447c] bg-[#00447c] px-3 py-1.5 text-[11.5px] font-semibold text-white hover:bg-[#003762] disabled:opacity-60"
          >
            <BookOpen className="h-3 w-3" /> Bowker Entry
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            data-testid="admin-isbn-save"
            className="inline-flex items-center gap-1 rounded-sm bg-primary px-3 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
          </button>
          {record.status !== 'discontinued' && (
            <button
              type="button"
              onClick={discontinue}
              disabled={busy}
              data-testid="admin-isbn-discontinue"
              className="inline-flex items-center gap-1 rounded-sm border border-amber-400 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900 hover:bg-amber-100 disabled:opacity-60"
            >
              <Lock className="h-3 w-3" /> Discontinue
            </button>
          )}
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            data-testid="admin-isbn-delete"
            className="inline-flex items-center gap-1 rounded-sm border border-rose-300 bg-rose-50 px-2 py-1.5 text-[11px] text-rose-800 hover:bg-rose-100 disabled:opacity-60"
          >
            <X className="h-3 w-3" /> Delete
          </button>
        </div>
      </div>

      {/* Metadata grid */}
      <div className="grid grid-cols-12 gap-3 overflow-y-auto pr-1">
        <Field label="Title" required className="col-span-8"
               value={draft.title || ''} onChange={(v) => set('title', v)} testid="admin-isbn-field-title" />
        <Field label="Subtitle" className="col-span-4"
               value={draft.subtitle || ''} onChange={(v) => set('subtitle', v)} testid="admin-isbn-field-subtitle" />

        <Field label="Contributor(s) (comma-separated)" className="col-span-8"
               value={draft._contributors_text ?? (draft.contributors || []).join(', ')}
               onChange={(v) => set('_contributors_text', v)}
               testid="admin-isbn-field-contributors" />
        <ReadOnlyField label="Publisher of Record" value={PUBLISHER}
                        className="col-span-4" testid="admin-isbn-field-publisher" />

        <SelectField label="Format & Binding" className="col-span-4"
                       value={draft.format || ''} onChange={(v) => set('format', v)}
                       options={FORMATS} testid="admin-isbn-field-format" />
        <Field label="Edition" className="col-span-4"
               value={draft.edition || ''} onChange={(v) => set('edition', v)}
               placeholder="First Edition" testid="admin-isbn-field-edition" />
        <Field label="Language" className="col-span-4"
               value={draft.language || ''} onChange={(v) => set('language', v)}
               placeholder="English" testid="admin-isbn-field-language" />

        <Field label="Trim size" className="col-span-4"
               value={draft.trim_size || ''} onChange={(v) => set('trim_size', v)}
               placeholder='6" × 9"' testid="admin-isbn-field-trim" />
        <Field label="Page count" className="col-span-4"
               value={draft.page_count == null ? '' : String(draft.page_count)}
               onChange={(v) => set('page_count', v)}
               type="number" placeholder="312" testid="admin-isbn-field-pages" />
        <Field label="Publication date" className="col-span-4"
               value={draft.publication_date || ''} onChange={(v) => set('publication_date', v)}
               type="date" testid="admin-isbn-field-pubdate" />

        <Field label="BISAC subject codes (comma-separated)" className="col-span-8"
               value={draft._bisac_text ?? (draft.bisac_codes || []).join(', ')}
               onChange={(v) => set('_bisac_text', v)}
               placeholder="FIC014000, FIC031000" testid="admin-isbn-field-bisac" />
        <Field label="Retail price (USD)" className="col-span-2"
               value={draft._retail_dollars ?? (record.retail_price_cents == null ? '' : (record.retail_price_cents / 100).toFixed(2))}
               onChange={(v) => set('_retail_dollars', v)}
               type="number" placeholder="17.99" testid="admin-isbn-field-price" />
        <Field label="Currency" className="col-span-2"
               value={draft.currency || 'USD'} onChange={(v) => set('currency', v)}
               placeholder="USD" testid="admin-isbn-field-currency" />

        <Field label="Linked The Loom project id" className="col-span-6"
               value={draft.project_id || ''} onChange={(v) => set('project_id', v)}
               placeholder="project_xxx" testid="admin-isbn-field-project" />
        <Field label="Linked Bookstore book id" className="col-span-6"
               value={draft.bookstore_book_id || ''} onChange={(v) => set('bookstore_book_id', v)}
               placeholder="book_xxx" testid="admin-isbn-field-book" />
      </div>

      {showBowker && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
                          zIndex: 90, overflow: 'auto', padding: '30px 20px' }}
              data-testid="bowker-modal">
          <div style={{ maxWidth: 820, margin: '0 auto',
                            background: '#fff', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                            borderRadius: 4 }}>
            <BowkerTitleEntry record={record}
                                  onClose={() => setShowBowker(false)}
                                  onSaved={onSaved} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────── widgets

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span data-testid={`admin-isbn-status-${status}`}
          className={`shrink-0 rounded-full border px-2 py-[1.5px] text-[10px] font-semibold ${s.cls}`}>
      {s.label}
    </span>
  );
}

/* iter-51p — Visual triage hint for admins: this record's project already
 * has at least one saved Canva cover, so issuing the ISBN will trigger
 * the cover-promotion path (see _maybe_promote_canva_cover in
 * routes_isbn.py). Records without this badge are still waiting on art.
 */
function CoverReadyBadge({ testid }) {
  return (
    <span
      data-testid={testid}
      title="A Canva cover is saved for this author's project — ready to publish."
      className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-fuchsia-300 bg-fuchsia-50 px-1.5 py-[1.5px] text-[10px] font-semibold text-fuchsia-900"
    >
      <ImageIcon className="h-2.5 w-2.5" /> Cover
    </span>
  );
}

/* iter-51q — Counterpart to CoverReadyBadge. Shows when the linked
 * project has zero chapter words, signalling the manuscript isn't ready
 * even if the cover art is. Admins can use these two badges side-by-side
 * to triage at-a-glance: both green = "issue now", anything missing =
 * "still cooking".
 */
function ManuscriptIncompleteBadge({ testid }) {
  return (
    <span
      data-testid={testid}
      title="The linked project has 0 chapter words — manuscript not ready."
      className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-[1.5px] text-[10px] font-semibold text-amber-900"
    >
      <FileText className="h-2.5 w-2.5" /> Draft
    </span>
  );
}

function Loader() {
  return (
    <div className="flex items-center gap-2 px-2 py-3 text-[12px] text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder = '',
                  className = '', type = 'text', required = false }) {
  return (
    <label className={`flex flex-col gap-0.5 ${className}`}>
      <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
        {label}{required ? ' *' : ''}
      </span>
      <input
        type={type}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange?.(e.target.value)}
        data-testid={testid}
        className="h-8 rounded-sm border border-border bg-background px-2 text-[12px] outline-none focus:border-primary"
      />
    </label>
  );
}

function ReadOnlyField({ label, value, className = '', testid }) {
  return (
    <label className={`flex flex-col gap-0.5 ${className}`}>
      <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <div data-testid={testid}
            className="h-8 truncate rounded-sm border border-border bg-muted px-2 py-1 text-[12px] text-muted-foreground">
        {value}
      </div>
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid, className = '' }) {
  return (
    <label className={`flex flex-col gap-0.5 ${className}`}>
      <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <select
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        data-testid={testid}
        className="h-8 rounded-sm border border-border bg-background px-2 text-[12px] outline-none focus:border-primary"
      >
        <option value="">—</option>
        {options.map((o) => <option key={o} value={o}>{o[0].toUpperCase() + o.slice(1)}</option>)}
      </select>
    </label>
  );
}


/**
 * Feb-2026 — Pool-aware allocation control.
 *
 * Renders a dropdown fed by `/admin/isbn/bank/pools` next to the
 * "Allocate from Bank" button so ops picks the block visually
 * instead of typing `?pool=` in the URL. Only pools with at least
 * one available ISBN are shown; an "Any pool (oldest first)" option
 * preserves the legacy behaviour.
 */
function IsbnAllocateWithPool({ onAllocate, busy }) {
  const [pools, setPools] = React.useState(null);
  const [selected, setSelected] = React.useState('');
  React.useEffect(() => {
    adminApi.isbnBankPools()
      .then((r) => setPools(r.pools || []))
      .catch(() => setPools([]));
  }, []);
  const available = (pools || []).filter((p) => (p.available || 0) > 0);
  return (
    <div className="inline-flex items-center gap-1"
         data-testid="admin-isbn-pool-selector">
      <select
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        disabled={busy || !pools}
        data-testid="admin-isbn-pool-dropdown"
        className="h-8 rounded-sm border border-border bg-background px-2 text-[11.5px] outline-none focus:border-primary"
      >
        <option value="">Any pool (oldest first)</option>
        {available.map((p) => (
          <option key={p.pool} value={p.pool}>
            {p.pool} ({p.available} available)
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => onAllocate(selected || undefined)}
        disabled={busy}
        data-testid="admin-isbn-allocate-from-bank"
        className="inline-flex items-center gap-1.5 rounded-sm border border-primary bg-background px-3 py-1.5 text-[11.5px] font-semibold text-primary hover:bg-primary/5 disabled:opacity-60"
        title={selected ? `Draw next ISBN from pool "${selected}"`
                          : 'Draw the next available ISBN from whichever pool is oldest'}
      >
        <Zap className="h-3 w-3" /> Allocate from Bank
      </button>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Sep-14-2026 — Bowker ISBN Bank inventory + paste-in wizard
// ═══════════════════════════════════════════════════════════════════
function IsbnBankSection({ onAllocateUpdated }) {
  const [bank, setBank]         = useState(null);
  const [pasteOpen, setPaste]   = useState(false);
  const [payload, setPayload]   = useState('');
  const [imprint, setImprint]   = useState('Crown & Rose');
  const [busy, setBusy]         = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await adminApi.isbnBowkerBankList('all');
      setBank(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bank load failed.');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const savePaste = async () => {
    if (!payload.trim()) return;
    setBusy(true);
    try {
      const r = await adminApi.isbnBankPaste(payload.trim(), imprint.trim());
      toast.success(
        `${r.counts.accepted} added · ${r.counts.rejected} rejected · ${r.counts.duplicate} duplicate`,
      );
      setPayload(''); setPaste(false); await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Paste failed.');
    } finally { setBusy(false); }
  };

  const exportOnix = async () => {
    setBusy(true);
    try {
      const res = await adminApi.isbnBowkerOnixExport();
      const blob = new Blob([res.data], { type: 'application/xml' });
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `crown-rose-bowker-onix-${stamp}.xml`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('Bowker ONIX 3.0 XML downloaded.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ONIX export failed.');
    } finally { setBusy(false); }
  };

  const counts = bank?.counts || {};
  const avail  = counts.available || 0;
  const allocated = counts.allocated || 0;
  const critical = avail === 0;
  const warn = avail > 0 && avail < 3;

  return (
    <div className="rounded-md border border-border bg-card/40 p-3"
         data-testid="admin-isbn-bank-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Wallet className="h-3.5 w-3.5 text-primary" />
          <span className="text-[13px] font-semibold">Bowker ISBN Bank</span>
          <span className={`rounded-full px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wider ${
            critical ? 'bg-red-500/15 text-red-700 dark:text-red-400'
            : warn   ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
                        : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
          }`} data-testid="admin-isbn-bank-count">
            {avail} available · {allocated} allocated
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setPaste(true)} disabled={busy}
                  data-testid="admin-isbn-bank-paste-open"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-primary bg-primary px-2.5 py-1 text-[11px] text-primary-foreground hover:opacity-90 disabled:opacity-60">
            <Plus className="h-3 w-3" /> Paste ISBN block
          </button>
          <button type="button" onClick={exportOnix} disabled={busy}
                  data-testid="admin-isbn-bowker-onix-download"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-background px-2.5 py-1 text-[11px] hover:bg-secondary disabled:opacity-60">
            <Download className="h-3 w-3" /> Download Bowker ONIX 3.0
          </button>
        </div>
      </div>
      {(critical || warn) && (
        <div className={`mt-2 text-[11px] ${critical ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
          {critical
            ? 'The Bank is empty. No further ISBNs can be allocated until you paste a new Bowker block.'
            : `Fewer than 3 ISBNs remain. Order the next block at myidentifiers.com and paste it in.`}
        </div>
      )}

      {pasteOpen && (
        <div className="mt-3 rounded-md border border-border bg-background p-3"
             data-testid="admin-isbn-bank-paste-dialog">
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-semibold">Paste a Bowker ISBN block</span>
            <button type="button" onClick={() => setPaste(false)} aria-label="Close"
                    data-testid="admin-isbn-bank-paste-close"
                    className="rounded-sm p-1 text-muted-foreground hover:bg-secondary">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="mt-1 text-[10.5px] text-muted-foreground">
            Newline / comma / space-separated ISBN-13s. Checksums verified server-side;
            duplicates against existing records are auto-skipped.
          </p>
          {/* 2026-02 · Bowker paste-in checklist — walks admins through
           *  the 5 manual steps since neither Bowker nor ISBN-US
           *  exposes a purchase API. Local-storage progress. */}
          <IsbnPasteChecklist />
          <textarea rows={5} value={payload} onChange={(e) => setPayload(e.target.value)}
                    placeholder="9780306406157&#10;9791091983754&#10;9781234567897"
                    className="mt-2 w-full rounded-sm border border-border bg-card px-2 py-1.5 font-mono text-[11px] outline-none focus:border-primary"
                    data-testid="admin-isbn-bank-paste-input"
          />
          <div className="mt-2 flex items-center justify-between gap-2">
            <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              Imprint:
              <input value={imprint} onChange={(e) => setImprint(e.target.value)}
                     className="h-7 rounded-sm border border-border bg-card px-1.5 text-[11px] outline-none focus:border-primary"
                     data-testid="admin-isbn-bank-paste-imprint"
              />
            </label>
            <button type="button" onClick={savePaste} disabled={busy || !payload.trim()}
                    data-testid="admin-isbn-bank-paste-save"
                    className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60">
              <Save className="h-3 w-3" /> Save block
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

